import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { extractInvitationToken, useCountdown } from '../account/helpers';
import { useApi } from '../auth/ApiProvider';
import { entryPath, useEntrySlug } from '../auth/entryPaths';
import { useSession } from '../auth/SessionProvider';
import { passkeysSupported } from '../auth/webauthn';
import { normalisePhone } from '../format';
import { useI18n } from '../i18n/I18nProvider';
import { CurrentPasswordField, Field, Input, NewPasswordFields, PhoneInput } from './Field';
import { PasskeyFailureNotice } from './Passkeys';
import { Button, Notice, Row, Stack } from './primitives';
import { LegalConsent } from './Legal';
import { AuthPreferences, BrandPanel, type BrandPanelProps } from './SignIn';
import { ErrorState, LoadingState } from './states';

/** The frame every pre-sign-in screen shares: brand, one card, the language switch. */
function AuthFrame({ brand, title, children }: { brand: BrandPanelProps; title: string; children: ReactNode }) {
  return (
    <div className="dg-auth">
      <div className="dg-auth__panel">
        <BrandPanel {...brand} />
        <div className="dg-card">
          <Stack>
            <h1 style={{ fontSize: 'var(--dg-text-xl)' }}>{title}</h1>
            {children}
          </Stack>
        </div>
        <AuthPreferences />
      </div>
    </div>
  );
}

function BackToSignIn() {
  const { t } = useI18n();
  return <Row><Link to={entryPath('sign-in', useEntrySlug())}>{t('auth.backToSignIn')}</Link></Row>;
}

/** A six-digit code field: numeric keypad, one-time-code autofill, left to right. */
export function CodeField({ value, onChange, error }: { value: string; onChange: (value: string) => void; error?: string | null }) {
  const { t } = useI18n();
  return (
    <Field label={t('auth.code')} hint={t('auth.code.hint')} error={error ?? null} required>
      {(props) => (
        <Input
          {...props}
          inputMode="numeric"
          autoComplete="one-time-code"
          dir="ltr"
          maxLength={6}
          value={value}
          onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
        />
      )}
    </Field>
  );
}

/** The state of the mutation that sends the code: whether it is sending, and how the last send failed. */
export interface CodeSend {
  isPending: boolean;
  error: unknown;
}

/** "Send the code" with its 60-second wait, and what came of the last send. */
function SendCodeButton({ onSend, send, outcome }: {
  onSend: () => void; send: CodeSend; outcome: 'sent' | 'failed' | null;
}) {
  const { t } = useI18n();
  return (
    <Stack tight>
      {outcome === 'sent' && <Notice tone="success">{t('auth.code.sent')}</Notice>}
      {outcome === 'failed' && <Notice tone="warning">{t('auth.code.failed')}</Notice>}
      <ResendTimer onSend={onSend} send={send} />
    </Stack>
  );
}

/**
 * "Send the code", disabled for the wait after each send, with the time left beside it. A send refused for coming
 * too often waits as long as the API said instead (`useCountdown`).
 */
export function ResendTimer({ onSend, send, startCounting = false }: {
  onSend: () => void; send: CodeSend;
  /** Start the wait straight away, for a screen shown just after a code was sent. */
  startCounting?: boolean;
}) {
  const { t } = useI18n();
  const { remaining, start } = useCountdown(send.error);

  useEffect(() => {
    if (startCounting) start();
  }, [startCounting, start]);

  return (
    <Row>
      <Button
        variant="secondary"
        busy={send.isPending}
        disabled={remaining > 0}
        onClick={() => { start(); onSend(); }}
      >
        {t('auth.code.send')}
      </Button>
      <CodeWait seconds={remaining} />
    </Row>
  );
}

/** "You can ask for a new code in …", in seconds up to a minute and in minutes past it; nothing at zero. */
export function CodeWait({ seconds }: { seconds: number }) {
  const { tn } = useI18n();
  if (seconds <= 0) return null;
  return (
    <span className="dg-muted dg-text-sm">
      {seconds > 60 ? tn('auth.code.resendInMinutes', Math.ceil(seconds / 60)) : tn('auth.code.resendIn', seconds)}
    </span>
  );
}

// ---- Accepting an invitation ----------------------------------------------------------------------------

/**
 * Redeems an invitation: paste the link (or arrive with it), see what it is for, prove the phone with a
 * texted code, choose a password, join (decision 2026-09-23 #9).
 *
 * The code is asked for every time. Someone whose number already has a Daghfal account signs in to it with
 * its password instead of choosing one: numbers are recycled, and the code proves only who holds the phone
 * today, not whose account it is (Q24). The preview says which applies.
 *
 * Someone holding a number whose account is not theirs says so (Q25). Where the preview allows it, they take
 * the number for a new account here; otherwise the screen says whom to ask, and the API decides either way.
 */
export function AcceptInvitationScreen({ redirectTo = '/', ...brand }: BrandPanelProps & { redirectTo?: string }) {
  const { t } = useI18n();
  const location = useLocation();
  const initial = extractInvitationToken(new URLSearchParams(location.search).get('token') ?? '');
  const [token, setToken] = useState<string | null>(initial);

  return (
    <AuthFrame brand={brand} title={t('auth.invitation.title')}>
      {token
        ? <InvitationSteps token={token} fromLink={token === initial} redirectTo={redirectTo} onRestart={() => setToken(null)} />
        : <PasteInvitation onToken={setToken} />}
      <BackToSignIn />
    </AuthFrame>
  );
}

function PasteInvitation({ onToken }: { onToken: (token: string) => void }) {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [invalid, setInvalid] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    const token = extractInvitationToken(text);
    setInvalid(token === null);
    if (token) onToken(token);
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        <Field
          label={t('auth.invitation.paste.label')}
          hint={t('auth.invitation.paste.hint')}
          error={invalid ? t('auth.invitation.paste.invalid') : null}
          required
        >
          {(props) => (
            <Input {...props} dir="ltr" value={text} onChange={(event) => setText(event.target.value)} autoFocus />
          )}
        </Field>
        <Button type="submit" block size="large">{t('auth.invitation.continue')}</Button>
      </Stack>
    </form>
  );
}

function InvitationSteps({ token, fromLink, redirectTo, onRestart }: {
  token: string;
  /** Whether the token came in the address, rather than pasted: only then can the address be corrected. */
  fromLink: boolean;
  redirectTo: string;
  onRestart: () => void;
}) {
  const api = useApi();
  const { t, locale, formatDate, formatPhone } = useI18n();
  const { acceptInvitation, releaseInvitedNumber } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const slug = useEntrySlug();

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);
  const [codeOutcome, setCodeOutcome] = useState<'sent' | 'failed' | null>(null);
  const [notMine, setNotMine] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreeError, setAgreeError] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ['invitation', 'preview', token],
    queryFn: () => api.invitations.preview(token),
    staleTime: Infinity,
  });

  // A link edited to carry another nursery's slug would show that nursery's name and logo beside this invitation,
  // so it moves to the invitation's own (Q64). A link with no slug stays where it is: moving it would remount the
  // form under someone already typing, for no gain. The platform console has no slugs.
  const canonical = preview.data?.tenantSlug ?? null;
  useEffect(() => {
    if (fromLink && slug && canonical && canonical !== slug) {
      navigate(`${entryPath('invitation', canonical)}${location.search}`, { replace: true });
    }
  }, [fromLink, canonical, slug, navigate, location.search]);

  const sendCode = useMutation({
    mutationFn: () => api.invitations.sendCode(token),
    onSuccess: (result) => setCodeOutcome(result.sms === 'failed' ? 'failed' : 'sent'),
  });

  // One field on the wire: the password to set on a new account, or the existing account's own. With a
  // passkey, the existing account is proven by the device instead, and no password is sent.
  const accept = useMutation({
    mutationFn: ({ withPasskey }: { withPasskey: boolean }) => acceptInvitation(
      token,
      withPasskey ? null : preview.data?.needsSignIn ? currentPassword : password,
      code,
      withPasskey,
      legalAcceptance(),
    ),
    onSuccess: () => navigate(redirectTo, { replace: true }),
  });

  const release = useMutation({
    mutationFn: () => releaseInvitedNumber(token, code, password, legalAcceptance()),
    onSuccess: () => navigate(redirectTo, { replace: true }),
  });

  if (preview.isPending) return <LoadingState />;
  if (preview.isError) {
    return (
      <Stack>
        <ErrorState error={preview.error} />
        <Row><Button variant="ghost" onClick={onRestart}>{t('action.back')}</Button></Row>
      </Stack>
    );
  }

  const { tenantName, fullName, maskedPhone, needsPassword, needsSignIn, expiresAt, releaseOption } = preview.data;
  const place = tenantName ?? t('auth.invitation.platformTeam');
  const releasing = needsSignIn && notMine;
  const canRelease = releasing && releaseOption === 'self';
  const choosesPassword = needsPassword || canRelease;
  const busy = accept.isPending || release.isPending;

  /** The versions the preview named, which the server checks are still the ones in force (Q67). */
  function legalAcceptance() {
    return {
      privacyVersion: preview.data!.privacyVersion,
      termsVersion: preview.data!.termsVersion,
      language: locale,
    };
  }

  /** Accepting the privacy notice and the terms is part of accepting an invitation (Q67). */
  function notAgreed() {
    setAgreeError(agreed ? null : t('legal.consent.required'));
    return !agreed;
  }

  /** Asked every time (Q24): whatever proves the account, the code proves who holds the phone today. */
  function codeMissing() {
    const missing = code.length !== 6;
    setCodeError(missing ? t('auth.code.required') : null);
    return missing;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (releasing && !canRelease) return;
    const missingCode = codeMissing();
    const unaccepted = notAgreed();
    const differs = choosesPassword && password !== confirmation;
    setMismatch(differs);
    if (canRelease) {
      if (!missingCode && !differs && !unaccepted) release.mutate();
      return;
    }
    const missingCurrent = needsSignIn && currentPassword.length === 0;
    setCurrentPasswordError(missingCurrent ? t('auth.password.required') : null);
    if (!missingCode && !differs && !missingCurrent && !unaccepted) accept.mutate({ withPasskey: false });
  }

  function acceptWithPasskey() {
    setCurrentPasswordError(null);
    const missingCode = codeMissing();
    if (!notAgreed() && !missingCode) accept.mutate({ withPasskey: true });
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        <p>{fullName
          ? t('auth.invitation.for', { place, name: fullName })
          : t('auth.invitation.forPlace', { place })}</p>
        <p className="dg-muted dg-text-sm">{t('auth.invitation.expires', { date: formatDate(expiresAt) })}</p>

        {accept.isError && <PasskeyFailureNotice error={accept.error} during="sign-in" />}
        {release.isError && <ErrorState error={release.error} />}

        {(!releasing || canRelease) && (
          <>
            <p>{t('auth.invitation.codeTo', { phone: formatPhone(maskedPhone) })}</p>
            {sendCode.isError && <ErrorState error={sendCode.error} />}
            <SendCodeButton onSend={() => sendCode.mutate()} send={sendCode} outcome={codeOutcome} />
            <CodeField value={code} onChange={setCode} error={codeError} />
          </>
        )}

        {needsSignIn && !notMine && (
          <>
            <Notice tone="info">{t('auth.invitation.existingAccount')}</Notice>
            <CurrentPasswordField
              label={t('auth.invitation.currentPassword')}
              error={currentPasswordError}
              value={currentPassword}
              onChange={setCurrentPassword}
            />
          </>
        )}

        {releasing && releaseOption === 'self' && <Notice tone="info">{t('auth.invitation.release.self')}</Notice>}
        {releasing && releaseOption === 'centre' && <Notice tone="warning">{t('auth.invitation.release.centre')}</Notice>}
        {releasing && releaseOption === 'platform' && <Notice tone="warning">{t('auth.invitation.release.platform')}</Notice>}

        {choosesPassword && (
          <NewPasswordFields
            labels={{
              password: t('auth.invitation.newPassword'),
              confirm: t('auth.invitation.confirmPassword'),
              mismatch: t('auth.invitation.mismatch'),
            }}
            password={password}
            confirmation={confirmation}
            onPassword={setPassword}
            onConfirmation={setConfirmation}
            mismatch={mismatch}
          />
        )}

        {(!releasing || canRelease) && (
          <>
            <LegalConsent checked={agreed} onChange={setAgreed} error={agreeError} />
            <Button
              type="submit" block size="large"
              busy={release.isPending || (accept.isPending && !accept.variables?.withPasskey)}
              disabled={busy}
              busyLabel={t('action.saving')}
            >
              {t(canRelease ? 'auth.invitation.release.submit' : 'auth.invitation.accept')}
            </Button>
          </>
        )}

        {needsSignIn && !notMine && passkeysSupported() && (
          <Button
            variant="secondary" block size="large"
            busy={accept.isPending && accept.variables?.withPasskey === true}
            disabled={busy}
            busyLabel={t('action.saving')}
            onClick={acceptWithPasskey}
          >
            {t('auth.invitation.usePasskey')}
          </Button>
        )}

        {needsSignIn && (
          <Button variant="ghost" block disabled={busy} onClick={() => setNotMine(!notMine)}>
            {t(notMine ? 'auth.invitation.mine' : 'auth.invitation.notYours')}
          </Button>
        )}
      </Stack>
    </form>
  );
}

// ---- Forgotten password ---------------------------------------------------------------------------------

/**
 * Resets a forgotten password with a code texted to the sign-in number (FR-AUTH-08, decision 2026-09-23
 * #13).
 *
 * The first step answers the same way whether or not the number has an account, so this screen says "if
 * the number is registered" rather than "we sent a code", which would tell anyone who types a number
 * whether it belongs to a family.
 *
 * A right code may still not reset at once (Q25): the reset is held while the account is open on another
 * device, or for an administrator, or waits for the nursery or the platform team to confirm the person. The
 * screen then says so; the person comes back later and asks for a fresh code.
 */
export function ForgotPasswordScreen(brand: BrandPanelProps) {
  const api = useApi();
  const { t, formatPhone, formatDateTime } = useI18n();

  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [requested, setRequested] = useState<{ phone: string; masked: string } | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const request = useMutation({
    mutationFn: (normalised: string) => api.account.requestPasswordReset(normalised),
    onSuccess: (result, normalised) => setRequested({ phone: normalised, masked: result.maskedPhone }),
  });
  // The first send has no countdown of its own, but a refusal for coming too often still holds it back.
  const { remaining: wait } = useCountdown(request.error);

  const confirm = useMutation({
    mutationFn: () => api.account.confirmPasswordReset(requested!.phone, code, password),
    onSettled: () => { setPassword(''); setConfirmation(''); },
  });

  function submitPhone(event: FormEvent) {
    event.preventDefault();
    const normalised = normalisePhone(phone);
    setPhoneError(normalised ? null : t('auth.phone.required'));
    if (normalised) request.mutate(normalised);
  }

  function submitReset(event: FormEvent) {
    event.preventDefault();
    const missingCode = code.length !== 6;
    const differs = password !== confirmation;
    setCodeError(missingCode ? t('auth.code.required') : null);
    setMismatch(differs);
    if (!missingCode && !differs) confirm.mutate();
  }

  if (confirm.isSuccess) {
    // A request back means it has to wait (Q25), and nothing changed.
    const waiting = confirm.data;
    return (
      <AuthFrame brand={brand} title={t('auth.reset.title')}>
        {!waiting && <Notice tone="success">{t('auth.reset.done')}</Notice>}
        {waiting?.route === 'Hold' && (
          <Notice tone="info">{t('auth.reset.held', { date: formatDateTime(waiting.availableAt) })}</Notice>
        )}
        {waiting?.route === 'Centre' && <Notice tone="info">{t('auth.reset.awaitingCentre')}</Notice>}
        {waiting?.route === 'Platform' && <Notice tone="info">{t('auth.reset.awaitingPlatform')}</Notice>}
        <BackToSignIn />
      </AuthFrame>
    );
  }

  return (
    <AuthFrame brand={brand} title={t('auth.reset.title')}>
      {!requested ? (
        <form onSubmit={submitPhone} noValidate>
          <Stack>
            <p className="dg-card__hint">{t('auth.reset.hint')}</p>
            {request.isError && <ErrorState error={request.error} />}
            <Field label={t('auth.phone')} hint={t('auth.phone.hint')} error={phoneError} required>
              {(props) => (
                <PhoneInput {...props} value={phone} onChange={(event) => setPhone(event.target.value)} autoFocus />
              )}
            </Field>
            <Button type="submit" block size="large" busy={request.isPending} busyLabel={t('action.saving')} disabled={wait > 0}>
              {t('auth.code.send')}
            </Button>
            <CodeWait seconds={wait} />
          </Stack>
        </form>
      ) : (
        <form onSubmit={submitReset} noValidate>
          <Stack>
            <Notice tone="info">{t('auth.reset.sent', { phone: formatPhone(requested.masked) })}</Notice>
            {request.isError && <ErrorState error={request.error} />}
            <ResendTimer onSend={() => request.mutate(requested.phone)} send={request} startCounting />
            {confirm.isError && <ErrorState error={confirm.error} />}
            <CodeField value={code} onChange={setCode} error={codeError} />
            <NewPasswordFields
              labels={{
                password: t('auth.reset.newPassword'),
                confirm: t('auth.reset.confirmPassword'),
                mismatch: t('auth.invitation.mismatch'),
              }}
              password={password}
              confirmation={confirmation}
              onPassword={setPassword}
              onConfirmation={setConfirmation}
              mismatch={mismatch}
            />
            <Button type="submit" block size="large" busy={confirm.isPending} busyLabel={t('action.saving')}>
              {t('auth.reset.submit')}
            </Button>
          </Stack>
        </form>
      )}
      <BackToSignIn />
    </AuthFrame>
  );
}

// ---- A recovery link from the platform team -------------------------------------------------------------

/**
 * Sets a new password with a one-time recovery link from the platform team (Q25). The team never sets or sees the
 * password (FR-AUTH-13); the person chooses it here. A platform member then adds a passkey before the console opens.
 */
export function RecoveryLinkScreen(brand: BrandPanelProps) {
  const api = useApi();
  const { t } = useI18n();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token')?.trim() ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const redeem = useMutation({
    mutationFn: () => api.account.redeemRecoveryLink(token, password),
    onSettled: () => { setPassword(''); setConfirmation(''); },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const differs = password !== confirmation;
    setMismatch(differs);
    if (!differs) redeem.mutate();
  }

  return (
    <AuthFrame brand={brand} title={t('auth.recoverLink.title')}>
      {!token && <Notice tone="warning">{t('auth.recoverLink.missing')}</Notice>}
      {token && redeem.isSuccess && <Notice tone="success">{t('auth.recoverLink.done')}</Notice>}
      {token && !redeem.isSuccess && (
        <form onSubmit={submit} noValidate>
          <Stack>
            <p className="dg-card__hint">{t('auth.recoverLink.hint')}</p>
            {redeem.isError && <ErrorState error={redeem.error} />}
            <NewPasswordFields
              labels={{
                password: t('auth.reset.newPassword'),
                confirm: t('auth.reset.confirmPassword'),
                mismatch: t('auth.invitation.mismatch'),
              }}
              password={password}
              confirmation={confirmation}
              onPassword={setPassword}
              onConfirmation={setConfirmation}
              mismatch={mismatch}
            />
            <Button type="submit" block size="large" busy={redeem.isPending} busyLabel={t('action.saving')}>
              {t('auth.recoverLink.submit')}
            </Button>
          </Stack>
        </form>
      )}
      <BackToSignIn />
    </AuthFrame>
  );
}

// ---- Confirming a new phone number ----------------------------------------------------------------------

/**
 * Opens a phone-change link (decision 2026-09-23 #12).
 *
 * A button rather than confirming on load: some messaging apps open links in the background to build a
 * preview, and that must not move somebody's account to a new number. Every session ends, so afterwards
 * the person signs in again with the new number.
 */
export function ConfirmPhoneChangeScreen(brand: BrandPanelProps) {
  const api = useApi();
  const { t, formatPhone } = useI18n();
  const { status, signOut } = useSession();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token')?.trim() ?? '';

  const confirm = useMutation({
    mutationFn: () => api.account.confirmPhoneChange(token),
    // The API has already ended every session; this clears the one stored on this device as well.
    onSuccess: async () => { if (status === 'authenticated') await signOut(); },
  });

  return (
    <AuthFrame brand={brand} title={t('account.phoneConfirm.title')}>
      {!token && <Notice tone="warning">{t('account.phoneConfirm.missing')}</Notice>}
      {token && confirm.isSuccess && (
        <Notice tone="success">{t('account.phoneConfirm.done', { phone: formatPhone(confirm.data.phone) })}</Notice>
      )}
      {token && !confirm.isSuccess && (
        <Stack>
          <p className="dg-card__hint">{t('account.phoneConfirm.hint')}</p>
          {confirm.isError && <ErrorState error={confirm.error} />}
          <Button block size="large" busy={confirm.isPending} busyLabel={t('action.saving')} onClick={() => confirm.mutate()}>
            {t('account.phoneConfirm.submit')}
          </Button>
        </Stack>
      )}
      <BackToSignIn />
    </AuthFrame>
  );
}
